// src/components/Navigation.js
// Version: 2.0 - Slide menu implementation
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import About from './About';
import axios from 'axios';

const Navigation = () => {
  const token = localStorage.getItem('token');
  const role  = localStorage.getItem('role');
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);
  const [driverAdminDropdownOpen, setDriverAdminDropdownOpen] = useState(false);
  const [supportDropdownOpen, setSupportDropdownOpen] = useState(false);
  const [aboutModalOpen, setAboutModalOpen] = useState(false);
  const [slideMenuOpen, setSlideMenuOpen] = useState(false);
  const [homeSlideMenuOpen, setHomeSlideMenuOpen] = useState(false);
  const [reportsExpanded, setReportsExpanded] = useState(false);
  const [adminExpanded, setAdminExpanded] = useState(false);
  const [supportExpanded, setSupportExpanded] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isNightMode, setIsNightMode] = useState(false);
  const [availableJobsCount, setAvailableJobsCount] = useState({ inMyArea: 0, outsideMyArea: 0, nationalJobs: 0 });
  
  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Fetch available jobs count for professional drivers
  useEffect(() => {
    const fetchAvailableJobsCount = async () => {
      if (token && role === 'professional') {
        try {
          const response = await axios.get('/job-monitor/available-jobs-count');
          setAvailableJobsCount({
            inMyArea: response.data.inMyArea || 0,
            outsideMyArea: response.data.outsideMyArea || 0,
            nationalJobs: response.data.nationalJobs || 0
          });
        } catch (error) {
          console.error('Error fetching available jobs count:', error);
          // Set to 0 on error
          setAvailableJobsCount({ inMyArea: 0, outsideMyArea: 0, nationalJobs: 0 });
        }
      }
    };

    fetchAvailableJobsCount();
    
    // Refresh count every 30 seconds
    const interval = setInterval(fetchAvailableJobsCount, 30000);
    
    return () => clearInterval(interval);
  }, [token, role]);

  // Monitor and fix text visibility in night mode
  useEffect(() => {
    if (isNightMode) {
      const fixTextVisibility = () => {
        const textElements = document.querySelectorAll('.accordion-body p, .accordion-body div, .accordion-body span, .accordion-body small, .accordion-body strong, .accordion-body label, .accordion-body input, .accordion-body textarea, .accordion-body h1, .accordion-body h2, .accordion-body h3, .accordion-body h4, .accordion-body h5, .accordion-body h6');
        textElements.forEach(element => {
          element.style.setProperty('color', '#e0e0e0', 'important');
          element.style.setProperty('background-color', 'transparent', 'important');
        });
      };

      // Fix immediately
      fixTextVisibility();
      
      // Set up observer for dynamically added content
      const observer = new MutationObserver(fixTextVisibility);
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      // Also fix periodically
      const interval = setInterval(fixTextVisibility, 1000);

      return () => {
        observer.disconnect();
        clearInterval(interval);
      };
    }
  }, [isNightMode]);

  // Restore night mode on page load
  useEffect(() => {
    const savedNightMode = localStorage.getItem('nightMode');
    if (savedNightMode === 'true') {
      setIsNightMode(true);
      document.body.style.backgroundColor = '#2c2c2c';
      document.body.style.color = '#ffffff';
      
      // Apply night mode styling with delay to ensure elements are rendered
      setTimeout(() => {
        // Accordion items
        const accordionItems = document.querySelectorAll('.accordion-item');
        accordionItems.forEach(item => {
          item.style.backgroundColor = '#404040';
          item.style.color = '#e0e0e0';
          item.style.borderColor = '#666666';
          
          // Preserve day separation borders (thick black lines)
          if (item.classList.contains('day-change-border')) {
            item.style.borderTop = '3px solid #000000 !important';
          }
        });
        
        // Accordion headers
        const accordionHeaders = document.querySelectorAll('.accordion-header');
        accordionHeaders.forEach(header => {
          header.style.backgroundColor = '#404040';
          header.style.color = '#e0e0e0';
          const button = header.querySelector('button');
          if (button) {
            button.style.backgroundColor = '#404040';
            button.style.color = '#e0e0e0';
            button.style.borderColor = '#666666';
          }
        });
        
        // Accordion bodies - ensure they get mid-grey background
        const accordionBodies = document.querySelectorAll('.accordion-body');
        accordionBodies.forEach(body => {
          body.style.backgroundColor = '#404040';
          body.style.color = '#e0e0e0';
          body.style.setProperty('background-color', '#404040', 'important');
        });
        
        // Section headers
        const sectionHeaders = document.querySelectorAll('h6.bg-dark');
        sectionHeaders.forEach(header => {
          header.style.backgroundColor = '#8c8c8c';
          header.style.color = '#ffffff';
        });
        
        // Fixed header
        const fixedHeader = document.querySelector('.position-fixed');
        if (fixedHeader) {
        fixedHeader.style.backgroundColor = '#404040';
        fixedHeader.style.color = '#e0e0e0';
        }
        
        // Fix text visibility in job cards - target all text elements and ensure proper contrast
        const textElements = document.querySelectorAll('.accordion-body p, .accordion-body div, .accordion-body span, .accordion-body small, .accordion-body strong, .accordion-body label, .accordion-body input, .accordion-body textarea');
        textElements.forEach(element => {
          element.style.setProperty('color', '#e0e0e0', 'important');
          element.style.setProperty('background-color', 'transparent', 'important');
        });
        
        // Ensure job card containers have darker background (excluding action buttons)
        const jobCardContainers = document.querySelectorAll('.accordion-body .card, .accordion-body .form-control');
        jobCardContainers.forEach(container => {
          container.style.backgroundColor = '#404040';
          container.style.color = '#e0e0e0';
          container.style.borderColor = '#666666';
        });
        
        // Reset action buttons to their original colors
        const actionButtons = document.querySelectorAll('.accordion-body .btn, .accordion-header .btn');
        actionButtons.forEach(button => {
          button.style.backgroundColor = '';
          button.style.color = '';
          button.style.borderColor = '';
          button.style.setProperty('background-color', 'unset', 'important');
          button.style.setProperty('color', 'unset', 'important');
          button.style.setProperty('border-color', 'unset', 'important');
        });
      }, 200);
    } else {
      // Ensure day mode styling is applied
      document.body.style.backgroundColor = '';
      document.body.style.color = '';
      
      // Set day mode styling with delay
      setTimeout(() => {
        // Set accordion items to white for day mode
        const accordionItems = document.querySelectorAll('.accordion-item');
        accordionItems.forEach(item => {
          item.style.backgroundColor = '#ffffff';
          item.style.color = '';
          item.style.borderColor = '';
        });
        
        // Set accordion headers to white for day mode
        const accordionHeaders = document.querySelectorAll('.accordion-header');
        accordionHeaders.forEach(header => {
          header.style.backgroundColor = '#ffffff';
          header.style.color = '';
          const button = header.querySelector('button');
          if (button) {
            button.style.backgroundColor = '#ffffff';
            button.style.color = '';
            button.style.borderColor = '';
          }
        });
        
        // Set accordion bodies to white for day mode
        const accordionBodies = document.querySelectorAll('.accordion-body');
        accordionBodies.forEach(body => {
          body.style.backgroundColor = '#ffffff';
          body.style.color = '';
        });
        
        // Reset all text elements to original colors in day mode
        const textElements = document.querySelectorAll('.accordion-body p, .accordion-body div, .accordion-body span, .accordion-body small, .accordion-body strong, .accordion-body label, .accordion-body input, .accordion-body textarea, .accordion-body h1, .accordion-body h2, .accordion-body h3, .accordion-body h4, .accordion-body h5, .accordion-body h6');
        textElements.forEach(element => {
          element.style.setProperty('color', 'unset', 'important');
          element.style.setProperty('background-color', 'unset', 'important');
        });
        
        // Reset section headers
        const sectionHeaders = document.querySelectorAll('h6.bg-dark');
        sectionHeaders.forEach(header => {
          header.style.backgroundColor = '';
          header.style.color = '';
        });
        
        // Set fixed header to white for day mode
        const fixedHeader = document.querySelector('.position-fixed');
        if (fixedHeader) {
          fixedHeader.style.backgroundColor = '#ffffff';
          fixedHeader.style.color = '';
        }
      }, 200);
    }
  }, []);
  
  const navCollapse = () => {
    document.querySelectorAll('.navbar-collapse.show').forEach(el => el.classList.remove('show'));
    setAdminDropdownOpen(false);
    setDriverAdminDropdownOpen(false);
    setSupportDropdownOpen(false);
    setSlideMenuOpen(false);
    setHomeSlideMenuOpen(false);
    // Always restore body scroll immediately when navigating
    requestAnimationFrame(() => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    });
  };
  
  const toggleHomeSlideMenu = () => {
    const newState = !homeSlideMenuOpen;
    setHomeSlideMenuOpen(newState);
    
    if (newState) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  };
  
  const closeHomeSlideMenu = () => {
    setHomeSlideMenuOpen(false);
    document.body.style.overflow = '';
  };
  
  const toggleSlideMenu = () => {
    console.log('Toggle slide menu clicked, current state:', slideMenuOpen);
    const newState = !slideMenuOpen;
    setSlideMenuOpen(newState);
    
    // Reset expanded states when closing menu
    if (!newState) {
      setReportsExpanded(false);
      setAdminExpanded(false);
      setSupportExpanded(false);
      // Restore body scroll when closing
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    } else {
      // Prevent body scroll when menu is open on mobile
      document.body.style.overflow = 'hidden';
    }
  };
  
  const closeSlideMenu = () => {
    setSlideMenuOpen(false);
    // Reset expanded states when closing
    setReportsExpanded(false);
    setAdminExpanded(false);
    setSupportExpanded(false);
    // Restore body scroll immediately
    requestAnimationFrame(() => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    });
  };
  
  // Restore body scroll immediately on mount and route changes
  useEffect(() => {
    // Restore body scroll immediately
    const restoreScroll = () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
    
    restoreScroll();
    
    // Also restore on any route change (React Router navigation)
    const handleRouteChange = () => {
      restoreScroll();
    };
    
    // Listen for navigation events
    window.addEventListener('popstate', handleRouteChange);
    
    // Use MutationObserver to detect when body styles might be changed
    const observer = new MutationObserver(() => {
      if (!slideMenuOpen && document.body.style.overflow === 'hidden') {
        restoreScroll();
      }
    });
    
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['style']
    });
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      observer.disconnect();
      restoreScroll();
    };
  }, [slideMenuOpen]);
  
  // Restore scroll immediately when slide menu closes
  useEffect(() => {
    if (!slideMenuOpen) {
      // Use requestAnimationFrame for immediate restoration
      requestAnimationFrame(() => {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
      });
    }
  }, [slideMenuOpen]);
  
  const navigate = useNavigate();
  const logout   = () => {
    localStorage.clear();
    // Dispatch custom event to reset vehicle expiry warning flag
    window.dispatchEvent(new Event('userLoggedOut'));
    navigate('/login');
  };

  const toggleAdminDropdown = (e) => {
    e.preventDefault();
    setAdminDropdownOpen(!adminDropdownOpen);
  };

  const toggleDriverAdminDropdown = (e) => {
    e.preventDefault();
    setDriverAdminDropdownOpen(!driverAdminDropdownOpen);
  };

  const toggleSupportDropdown = (e) => {
    e.preventDefault();
    setSupportDropdownOpen(!supportDropdownOpen);
  };

  // Format time for digital clock display
  const formatTime = (date) => {
    return date.toLocaleTimeString('en-GB', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).replace(/\s(AM|PM)/i, ''); // Remove AM/PM
  };

  // Add CSS for day separation borders and proper day/night mode styling
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'day-separation-styles';
    styleSheet.textContent = `
      .accordion-item.day-change-border {
        border-top: 3px solid #000000 !important;
      }
      
      /* Night mode styling - darker colors, no white */
      body[style*="background-color: rgb(44, 44, 44)"] .accordion-body {
        background-color: #404040 !important;
      }
      
      body[style*="background-color: rgb(44, 44, 44)"] .accordion-header {
        background-color: #404040 !important;
      }
      
      body[style*="background-color: rgb(44, 44, 44)"] .accordion-item {
        background-color: #404040 !important;
      }
      
      body[style*="background-color: rgb(44, 44, 44)"] .accordion-body * {
        color: #e0e0e0 !important;
      }
      
      body[style*="background-color: rgb(44, 44, 44)"] .accordion-header * {
        color: #e0e0e0 !important;
      }
      
      body[style*="background-color: rgb(44, 44, 44)"] .accordion-body .btn {
        color: unset !important;
        background-color: unset !important;
        border-color: unset !important;
      }
      
      body[style*="background-color: rgb(44, 44, 44)"] .accordion-header .btn {
        color: unset !important;
        background-color: unset !important;
        border-color: unset !important;
      }
      
      /* Force button colors to stay original in night mode */
      body[style*="background-color: rgb(44, 44, 44)"] .btn-primary {
        background-color: #0d6efd !important;
        border-color: #0d6efd !important;
        color: #ffffff !important;
      }
      
      body[style*="background-color: rgb(44, 44, 44)"] .btn-success {
        background-color: #198754 !important;
        border-color: #198754 !important;
        color: #ffffff !important;
      }
      
      body[style*="background-color: rgb(44, 44, 44)"] .btn-warning {
        background-color: #ffc107 !important;
        border-color: #ffc107 !important;
        color: #000000 !important;
      }
      
      body[style*="background-color: rgb(44, 44, 44)"] .btn-danger {
        background-color: #dc3545 !important;
        border-color: #dc3545 !important;
        color: #ffffff !important;
      }
      
      body[style*="background-color: rgb(44, 44, 44)"] .btn-info {
        background-color: #0dcaf0 !important;
        border-color: #0dcaf0 !important;
        color: #000000 !important;
      }
      
      body[style*="background-color: rgb(44, 44, 44)"] .btn-secondary {
        background-color: #6c757d !important;
        border-color: #6c757d !important;
        color: #ffffff !important;
      }
      
      body[style*="background-color: rgb(44, 44, 44)"] .accordion-body .form-control {
        background-color: #404040 !important;
        color: #e0e0e0 !important;
        border-color: #666666 !important;
      }
      
      /* Day mode - ensure original colors are preserved */
      body:not([style*="background-color: rgb(44, 44, 44)"]) .accordion-body {
        background-color: #ffffff !important;
      }
      
      body:not([style*="background-color: rgb(44, 44, 44)"]) .accordion-header {
        background-color: #ffffff !important;
      }
      
      body:not([style*="background-color: rgb(44, 44, 44)"]) .accordion-item {
        background-color: #ffffff !important;
      }
      
      body:not([style*="background-color: rgb(44, 44, 44)"]) .accordion-body * {
        color: unset !important;
      }
      
      body:not([style*="background-color: rgb(44, 44, 44)"]) .accordion-header * {
        color: unset !important;
      }
      
      /* Right Slide Menu Styles */
      .slide-menu-backdrop {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 9999;
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.3s ease, visibility 0.3s ease;
        -webkit-tap-highlight-color: transparent;
      }
      
      .slide-menu-backdrop.show {
        opacity: 1;
        visibility: visible;
      }
      
      .slide-menu {
        position: fixed;
        top: 0;
        right: -350px;
        width: 350px;
        max-width: 85vw;
        height: 100vh;
        height: 100dvh; /* Dynamic viewport height for mobile */
        background-color: #ffc107;
        z-index: 10000;
        box-shadow: -2px 0 10px rgba(0, 0, 0, 0.2);
        transition: right 0.3s ease;
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
        touch-action: pan-y; /* Allow vertical scrolling */
      }
      
      .slide-menu.open {
        right: 0;
      }
      
      .slide-menu-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1rem 1.5rem;
        background-color: #ffc107;
        border-bottom: 1px solid rgba(0, 0, 0, 0.1);
        position: sticky;
        top: 0;
        z-index: 1;
      }
      
      .slide-menu-header h5 {
        margin: 0;
        font-weight: bold;
        color: #000;
      }
      
      .slide-menu-header .btn-close {
        background: none;
        border: none;
        font-size: 2rem;
        cursor: pointer;
        opacity: 0.7;
        padding: 0;
        width: 2rem;
        height: 2rem;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #000;
        font-weight: normal;
        line-height: 1;
        transition: all 0.2s ease;
      }
      
      .slide-menu-header .btn-close:hover {
        opacity: 1;
        background-color: rgba(0, 0, 0, 0.1);
        border-radius: 0.25rem;
      }
      
      .slide-menu-body {
        flex: 1;
        padding: 0.5rem 0;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
      }
      
      .slide-menu-footer {
        margin-top: auto;
        padding: 1rem;
        border-top: 1px solid rgba(0, 0, 0, 0.1);
        background-color: rgba(0, 0, 0, 0.02);
      }
      
      .slide-menu-logout-btn {
        width: 100%;
        padding: 0.75rem 1rem;
        background-color: #dc3545;
        color: #ffffff;
        border: none;
        border-radius: 0.25rem;
        font-weight: 600;
        font-size: 1rem;
        cursor: pointer;
        transition: background-color 0.2s ease;
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
        touch-action: manipulation;
        min-height: 44px;
      }
      
      .slide-menu-logout-btn:hover {
        background-color: #c82333;
      }
      
      .slide-menu-logout-btn:active {
        background-color: #bd2130;
      }
      
      .slide-menu-section {
        margin-bottom: 0.25rem;
      }
      
      .slide-menu-section-title {
        padding: 0.5rem 1rem;
        margin: 0;
        font-size: 0.875rem;
        font-weight: bold;
        text-transform: uppercase;
        color: #000;
        opacity: 0.7;
        letter-spacing: 0.5px;
        cursor: pointer;
        display: flex;
        justify-content: space-between;
        align-items: center;
        user-select: none;
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
        touch-action: manipulation;
        transition: background-color 0.2s ease;
      }
      
      .slide-menu-section-title:hover {
        background-color: rgba(0, 0, 0, 0.05);
        opacity: 0.9;
      }
      
      .slide-menu-section-title .collapse-icon {
        font-size: 0.75rem;
        transition: transform 0.2s ease;
      }
      
      .slide-menu-section-title.expanded .collapse-icon {
        transform: rotate(90deg);
      }
      
      .slide-menu-section-content {
        max-height: 0;
        overflow: hidden;
        transition: max-height 0.3s ease;
      }
      
      .slide-menu-section-content.expanded {
        max-height: 1000px;
      }
      
      .slide-menu-item {
        display: block;
        padding: 0.5rem 1rem 0.5rem 1.5rem;
        color: #000;
        text-decoration: none;
        transition: background-color 0.2s ease;
        border-left: 3px solid transparent;
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
        touch-action: manipulation;
        min-height: 40px;
        display: flex;
        align-items: center;
      }
      
      .slide-menu-item:hover {
        background-color: rgba(0, 0, 0, 0.1);
        border-left-color: #000;
        color: #000;
        text-decoration: none;
      }
      
      .slide-menu-item:active {
        background-color: rgba(0, 0, 0, 0.15);
      }
      
      .slide-menu-header .btn-close {
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
        touch-action: manipulation;
        min-width: 44px;
        min-height: 44px;
      }
      
      @media (max-width: 576px) {
        .slide-menu {
          width: 280px;
          max-width: 85vw;
          right: -280px;
        }
        
        .slide-menu-header {
          padding: 1rem;
        }
        
        .slide-menu-item {
          padding: 0.5rem 1rem 0.5rem 1.5rem;
          font-size: 0.95rem;
        }
        
        .slide-menu-section-title {
          padding: 0.5rem 1rem;
          font-size: 0.8rem;
        }
      }
      
      /* Ensure menu is above everything on mobile */
      @media (max-width: 768px) {
        .slide-menu-backdrop {
          z-index: 9999;
        }
        
        .slide-menu {
          z-index: 10000;
        }
        
        /* Prevent body scroll when menu is open */
        body.menu-open {
          overflow: hidden;
          position: fixed;
          width: 100%;
        }
      }
      
      /* Improve touch targets on mobile */
      @media (hover: none) and (pointer: coarse) {
        .slide-menu-item {
          padding: 1rem 1.5rem;
        }
        
        .slide-menu-header .btn-close {
          padding: 0.5rem;
        }
      }
      
    `;
    document.head.appendChild(styleSheet);
    
    return () => {
      const existingStyle = document.getElementById('day-separation-styles');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  // Toggle night mode for clock and apply to body
  const toggleNightMode = () => {
    const newNightMode = !isNightMode;
    setIsNightMode(newNightMode);
    
    // Save preference to localStorage
    localStorage.setItem('nightMode', newNightMode.toString());
    
    // Apply night mode to body background and accordion styling
    if (newNightMode) {
      document.body.style.backgroundColor = '#2c2c2c';
      document.body.style.color = '#ffffff';
      
      // Apply night mode to all accordion elements with more specific targeting
      setTimeout(() => {
        // Accordion items
        const accordionItems = document.querySelectorAll('.accordion-item');
        accordionItems.forEach(item => {
          item.style.backgroundColor = '#404040';
          item.style.color = '#e0e0e0';
          item.style.borderColor = '#666666';
          
          // Preserve day separation borders (thick black lines)
          if (item.classList.contains('day-change-border')) {
            item.style.borderTop = '3px solid #000000 !important';
          }
        });
        
        // Accordion headers (both collapsed and expanded)
        const accordionHeaders = document.querySelectorAll('.accordion-header');
        accordionHeaders.forEach(header => {
          header.style.backgroundColor = '#404040';
          header.style.color = '#e0e0e0';
          // Target the button inside the header
          const button = header.querySelector('button');
          if (button) {
            button.style.backgroundColor = '#404040';
            button.style.color = '#e0e0e0';
            button.style.borderColor = '#666666';
          }
        });
        
        // Accordion bodies - ensure they get mid-grey background
        const accordionBodies = document.querySelectorAll('.accordion-body');
        accordionBodies.forEach(body => {
          body.style.backgroundColor = '#404040';
          body.style.color = '#e0e0e0';
          body.style.setProperty('background-color', '#404040', 'important');
        });
        
        // Section headers
        const sectionHeaders = document.querySelectorAll('h6.bg-dark');
        sectionHeaders.forEach(header => {
          header.style.backgroundColor = '#8c8c8c';
          header.style.color = '#ffffff';
        });
        
        // Fixed header
        const fixedHeader = document.querySelector('.position-fixed');
        if (fixedHeader) {
        fixedHeader.style.backgroundColor = '#404040';
        fixedHeader.style.color = '#e0e0e0';
        }
        
        // Fix text visibility in job cards - target all text elements and ensure proper contrast
        const textElements = document.querySelectorAll('.accordion-body p, .accordion-body div, .accordion-body span, .accordion-body small, .accordion-body strong, .accordion-body label, .accordion-body input, .accordion-body textarea');
        textElements.forEach(element => {
          element.style.setProperty('color', '#e0e0e0', 'important');
          element.style.setProperty('background-color', 'transparent', 'important');
        });
        
        // Ensure job card containers have darker background (excluding action buttons)
        const jobCardContainers = document.querySelectorAll('.accordion-body .card, .accordion-body .form-control');
        jobCardContainers.forEach(container => {
          container.style.backgroundColor = '#404040';
          container.style.color = '#e0e0e0';
          container.style.borderColor = '#666666';
        });
        
        // Reset action buttons to their original colors
        const actionButtons = document.querySelectorAll('.accordion-body .btn, .accordion-header .btn');
        actionButtons.forEach(button => {
          button.style.backgroundColor = '';
          button.style.color = '';
          button.style.borderColor = '';
          button.style.setProperty('background-color', 'unset', 'important');
          button.style.setProperty('color', 'unset', 'important');
          button.style.setProperty('border-color', 'unset', 'important');
        });
      }, 100); // Small delay to ensure elements are rendered
    } else {
      document.body.style.backgroundColor = '';
      document.body.style.color = '';
      
      // Reset all accordion elements to white
      setTimeout(() => {
        // Reset accordion items
        const accordionItems = document.querySelectorAll('.accordion-item');
        accordionItems.forEach(item => {
          item.style.backgroundColor = '#ffffff';
          item.style.color = '';
          item.style.borderColor = '';
        });
        
        // Reset accordion headers
        const accordionHeaders = document.querySelectorAll('.accordion-header');
        accordionHeaders.forEach(header => {
          header.style.backgroundColor = '#ffffff';
          header.style.color = '';
          const button = header.querySelector('button');
          if (button) {
            button.style.backgroundColor = '#ffffff';
            button.style.color = '';
            button.style.borderColor = '';
          }
        });
        
        // Reset accordion bodies to white
        const accordionBodies = document.querySelectorAll('.accordion-body');
        accordionBodies.forEach(body => {
          body.style.backgroundColor = '#ffffff';
          body.style.color = '';
          body.style.setProperty('background-color', '#ffffff', 'important');
        });
        
        // Reset all text elements to original colors in day mode
        const textElements = document.querySelectorAll('.accordion-body p, .accordion-body div, .accordion-body span, .accordion-body small, .accordion-body strong, .accordion-body label, .accordion-body input, .accordion-body textarea, .accordion-body h1, .accordion-body h2, .accordion-body h3, .accordion-body h4, .accordion-body h5, .accordion-body h6');
        textElements.forEach(element => {
          element.style.setProperty('color', 'unset', 'important');
          element.style.setProperty('background-color', 'unset', 'important');
        });
        
        // Reset section headers
        const sectionHeaders = document.querySelectorAll('h6.bg-dark');
        sectionHeaders.forEach(header => {
          header.style.backgroundColor = '';
          header.style.color = '';
        });
        
        // Reset fixed header
        const fixedHeader = document.querySelector('.position-fixed');
        if (fixedHeader) {
          fixedHeader.style.backgroundColor = '#ffffff';
          fixedHeader.style.color = '';
        }
        
        // Reset job card containers to default styling
        const jobCardContainers = document.querySelectorAll('.accordion-body .card, .accordion-body .form-control');
        jobCardContainers.forEach(container => {
          container.style.backgroundColor = '';
          container.style.color = '';
          container.style.borderColor = '';
        });
        
        // Reset action buttons to their original colors in day mode
        const actionButtons = document.querySelectorAll('.accordion-body .btn, .accordion-header .btn');
        actionButtons.forEach(button => {
          button.style.backgroundColor = '';
          button.style.color = '';
          button.style.borderColor = '';
          button.style.setProperty('background-color', 'unset', 'important');
          button.style.setProperty('color', 'unset', 'important');
          button.style.setProperty('border-color', 'unset', 'important');
        });
      }, 100);
    }
  };

  return (
    <nav className="navbar navbar-expand-md navbar-light bg-warning shadow-sm mb-4 sticky-top" style={{ zIndex: 1030 }}>
      <div className="container">
        <div className="d-flex align-items-center w-100">
          <Link className="navbar-brand" to="/home" onClick={navCollapse}>
            Taxi-Local.uk
          </Link>
          {/* Digital Clock - visible on mobile */}
          <div className="d-md-none ms-auto me-2 d-flex align-items-center">
            <div 
              className="btn btn-outline-dark rounded d-flex align-items-center justify-content-center" 
              onClick={toggleNightMode}
              style={{ 
                fontFamily: 'monospace', 
                fontSize: '1.2rem',
                fontWeight: 'bold',
                padding: '0.5rem 0.75rem',
                border: '1px solid #6c757d',
                backgroundColor: isNightMode ? '#2c2c2c' : 'rgba(255,255,255,0.8)',
                color: isNightMode ? '#ffffff' : '#000000',
                minWidth: '80px',
                height: '38px', // Match navbar-toggler height
                lineHeight: '1',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              {formatTime(currentTime)}
            </div>
            {/* Home Menu Button - visible on mobile when NOT logged in */}
            {!token && (
              <button 
                type="button"
                className="btn btn-link nav-link d-md-none ms-2"
                onClick={(e) => {
                  e.preventDefault();
                  toggleHomeSlideMenu();
                }}
                style={{ 
                  border: 'none', 
                  background: 'none',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '1.5rem',
                  color: '#000',
                  padding: '0.25rem 0.5rem',
                  minWidth: '44px',
                  minHeight: '44px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: '1'
                }}
                aria-label="Open menu"
              >
                ☰
              </button>
            )}
          </div>
          {/* Menu button for drivers - always visible on mobile */}
          {token && role === 'professional' && (
            <button 
              type="button"
              className="btn btn-link nav-link d-md-none ms-2"
              onClick={(e) => {
                e.preventDefault();
                toggleSlideMenu();
              }}
              style={{ 
                border: 'none', 
                background: 'none',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '1rem',
                color: '#000',
                padding: '0.5rem',
                minWidth: '44px',
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              aria-label="Open menu"
            >
              ☰
            </button>
          )}
          {/* Menu button for admin - always visible on mobile */}
          {token && role === 'admin' && (
            <button 
              type="button"
              className="btn btn-link nav-link d-md-none ms-2"
              onClick={(e) => {
                e.preventDefault();
                toggleSlideMenu();
              }}
              style={{ 
                border: 'none', 
                background: 'none',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '1rem',
                color: '#000',
                padding: '0.5rem',
                minWidth: '44px',
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              aria-label="Open menu"
            >
              ☰
            </button>
          )}
        </div>
        <div className="collapse navbar-collapse" id="mainNavbar">
          <ul className="navbar-nav me-auto">
            {!token ? (
              <>
                <li className="nav-item">
                  <Link to="/home" className="nav-link" onClick={navCollapse}>Home</Link>
                </li>
                <li className="nav-item">
                  <Link to="/where-is-my-taxi" className="nav-link" onClick={navCollapse}>
                    Where Is My Taxi?
                  </Link>
                </li>
                <li className="nav-item">
                  <Link to="/my-bookings" className="nav-link" onClick={navCollapse}>My Bookings</Link>
                </li>
                <li className="nav-item">
                  <Link to="/become-a-member" className="nav-link" onClick={navCollapse}>
                    Operators Join Our Growing Network
                  </Link>
                </li>
                <li className="nav-item">
                  <button
                    className="nav-link btn btn-link"
                    onClick={() => {
                      navCollapse();
                      setAboutModalOpen(true);
                    }}
                    style={{ border: 'none', background: 'none', textDecoration: 'none' }}
                  >
                    About
                  </button>
                </li>
                <li className="nav-item">
                  <Link to="/login" className="nav-link" onClick={navCollapse}>Driver Login</Link>
                </li>
              </>
            ) : (
              <>
                <li className="nav-item">
                  <Link to="/home" className="nav-link" onClick={navCollapse}>Home</Link>
                </li>
                <li className="nav-item">
                  <button
                    className="nav-link btn btn-link"
                    onClick={() => {
                      navCollapse();
                      setAboutModalOpen(true);
                    }}
                    style={{ border: 'none', background: 'none', textDecoration: 'none' }}
                  >
                    About
                  </button>
                </li>
                <li className="nav-item">
                  <Link to="/where-is-my-taxi" className="nav-link" onClick={navCollapse}>
                    Where Is My Taxi?
                  </Link>
                </li>
              </>
            )}
            {token && role === 'professional' && <>
              <li className="nav-item">
                <Link to="/dashboard" className="nav-link" onClick={navCollapse}>Available Jobs</Link>
              </li>
              <li className="nav-item">
                <Link to="/dashboard/accepted-jobs" className="nav-link" onClick={navCollapse}>My Diary</Link>
              </li>
              <li className="nav-item">
                <Link to="/dashboard/taxi-meter" className="nav-link" onClick={navCollapse}>Taxi Meter</Link>
              </li>
              <li className="nav-item">
                <Link to="/dashboard/completed-jobs" className="nav-link" onClick={navCollapse}>Completed Jobs</Link>
              </li>
              <li className="nav-item">
                <Link to="/dashboard/uncompleted-jobs" className="nav-link" onClick={navCollapse}>Uncompleted Jobs</Link>
              </li>
              <li className="nav-item">
                <Link to="/dashboard/reassigned-jobs" className="nav-link" onClick={navCollapse}>Re-Assigned</Link>
              </li>
              <li className="nav-item d-none d-md-block">
                <button 
                  type="button"
                  className="nav-link btn btn-link p-0"
                  onClick={toggleSlideMenu}
                  style={{ 
                    border: 'none', 
                    background: 'none',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '1rem',
                    color: '#000'
                  }}
                >
                  ☰ Menu
                </button>
              </li>
            </>}
            {token && role === 'listing_only' && <>
              <li className="nav-item">
                <Link to="/dashboard" className="nav-link" onClick={navCollapse}>Available Jobs</Link>
              </li>
              <li className="nav-item dropdown">
                <button 
                  type="button"
                  className="nav-link dropdown-toggle btn btn-link p-0"
                  onClick={toggleDriverAdminDropdown}
                  aria-expanded={driverAdminDropdownOpen}
                >
                  Account
                </button>
                <ul
                  className={`dropdown-menu ${driverAdminDropdownOpen ? 'show' : ''}`}
                  style={{ backgroundColor: '#ffc107', border: 'none', boxShadow: 'none' }}
                >
                  <li>
                    <Link to="/dashboard/update-account" className="dropdown-item" onClick={navCollapse} style={{ paddingLeft: '2rem' }}>
                      Profile
                    </Link>
                  </li>
                  <li>
                    <Link to="/dashboard/job-monitor" className="dropdown-item" onClick={navCollapse} style={{ paddingLeft: '2rem' }}>
                      Job Monitor Settings
                    </Link>
                  </li>
                </ul>
              </li>
              <li className="nav-item dropdown">
                <button 
                  type="button"
                  className="nav-link dropdown-toggle btn btn-link p-0"
                  onClick={toggleSupportDropdown}
                  aria-expanded={supportDropdownOpen}
                >
                  Support
                </button>
                <ul
                  className={`dropdown-menu ${supportDropdownOpen ? 'show' : ''}`}
                  style={{ backgroundColor: '#ffc107', border: 'none', boxShadow: 'none' }}
                >
                  <li>
                    <Link to="/dashboard/training" className="dropdown-item" onClick={navCollapse} style={{ paddingLeft: '2rem' }}>
                      Training
                    </Link>
                  </li>
                  <li>
                    <Link to="/dashboard/troubleshooting" className="dropdown-item" onClick={navCollapse} style={{ paddingLeft: '2rem' }}>
                      Troubleshooting
                    </Link>
                  </li>
                  <li>
                    <Link to="/dashboard/downloads" className="dropdown-item" onClick={navCollapse} style={{ paddingLeft: '2rem' }}>
                      Downloads
                    </Link>
                  </li>
                </ul>
              </li>
            </>}
            {token && role === 'admin' && <>
              <li className="nav-item dropdown">
                <button 
                  type="button"
                  className="nav-link dropdown-toggle btn btn-link p-0"
                  onClick={toggleAdminDropdown}
                  aria-expanded={adminDropdownOpen}
                >
                  Admin
                </button>
                <ul className={`dropdown-menu ${adminDropdownOpen ? 'show' : ''}`}>
                  <li>
                    <Link to="/admin/system" className="dropdown-item" onClick={navCollapse}>
                      System Administration
                    </Link>
                  </li>
                  <li>
                    <Link to="/admin/audio-test" className="dropdown-item" onClick={navCollapse}>
                      🔊 Audio Test
                    </Link>
                  </li>
                  <li>
                    <Link to="/dashboard/dispatch" className="dropdown-item" onClick={navCollapse}>
                      🚛 Dispatch Module
                    </Link>
                  </li>
                </ul>
              </li>
            </>}
            {token && role === 'dispatch' && (
              <li className="nav-item">
                <Link to="/dashboard/dispatch" className="nav-link" onClick={navCollapse}>
                  🚛 Dispatch
                </Link>
              </li>
            )}
          </ul>
          {token && (
            <button className="btn btn-sm btn-outline-danger" onClick={logout}>
              Logout
            </button>
          )}
        </div>
      </div>
      
      {/* About Modal */}
      <About show={aboutModalOpen} onHide={() => setAboutModalOpen(false)} />
      
      {/* Right Slide Menu for Home Page (Not Logged In) */}
      {!token && (
        <>
          {/* Backdrop */}
          <div 
            className={`slide-menu-backdrop ${homeSlideMenuOpen ? 'show' : ''}`}
            onClick={closeHomeSlideMenu}
          />
          
          {/* Slide Menu */}
          <div className={`slide-menu ${homeSlideMenuOpen ? 'open' : ''}`}>
            <div className="slide-menu-header">
              <h5 className="mb-0">Menu</h5>
              <button 
                type="button"
                className="btn-close"
                onClick={closeHomeSlideMenu}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            
            <div className="slide-menu-body">
              <Link to="/fare-calculator" className="slide-menu-item" onClick={navCollapse}>
                Fare Estimation
              </Link>
              <Link to="/where-is-my-taxi" className="slide-menu-item" onClick={navCollapse}>
                Where's My Taxi
              </Link>
              <Link to="/my-bookings" className="slide-menu-item" onClick={navCollapse}>
                My Bookings
              </Link>
              <Link to="/become-a-member" className="slide-menu-item" onClick={navCollapse}>
                Operators Join our Growing Network
              </Link>
              <button
                className="slide-menu-item"
                onClick={() => {
                  navCollapse();
                  setAboutModalOpen(true);
                }}
                style={{ 
                  border: 'none', 
                  background: 'none', 
                  width: '100%',
                  textAlign: 'left',
                  cursor: 'pointer'
                }}
              >
                About
              </button>
            </div>
            
            <div className="slide-menu-footer">
              <Link 
                to="/login" 
                className="slide-menu-logout-btn"
                onClick={navCollapse}
                style={{
                  textDecoration: 'none',
                  display: 'block'
                }}
              >
                Driver Login
              </Link>
            </div>
          </div>
        </>
      )}
      
      {/* Right Slide Menu for Admin */}
      {token && role === 'admin' && (
        <>
          {/* Backdrop */}
          <div 
            className={`slide-menu-backdrop ${slideMenuOpen ? 'show' : ''}`}
            onClick={closeSlideMenu}
          />
          
          {/* Slide Menu */}
          <div className={`slide-menu ${slideMenuOpen ? 'open' : ''}`}>
            <div className="slide-menu-header">
              <h5 className="mb-0">Menu</h5>
              <button 
                type="button"
                className="btn-close"
                onClick={closeSlideMenu}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            
            <div className="slide-menu-body">
              <Link to="/admin/system" className="slide-menu-item" onClick={navCollapse}>
                System Administration
              </Link>
              <Link to="/admin/audio-test" className="slide-menu-item" onClick={navCollapse}>
                Audio Test
              </Link>
              <Link to="/dashboard/dispatch" className="slide-menu-item" onClick={navCollapse}>
                Dispatch Module
              </Link>
            </div>
            
            <div className="slide-menu-footer">
              <button 
                className="slide-menu-logout-btn"
                onClick={() => {
                  closeSlideMenu();
                  logout();
                }}
              >
                Logout
              </button>
            </div>
          </div>
        </>
      )}
      
      {/* Right Slide Menu for Drivers */}
      {token && role === 'professional' && (
        <>
          {/* Backdrop */}
          <div 
            className={`slide-menu-backdrop ${slideMenuOpen ? 'show' : ''}`}
            onClick={closeSlideMenu}
          />
          
          {/* Slide Menu */}
          <div className={`slide-menu ${slideMenuOpen ? 'open' : ''}`}>
            <div className="slide-menu-header">
              <h5 className="mb-0">Menu</h5>
              <button 
                type="button"
                className="btn-close"
                onClick={closeSlideMenu}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            
            <div className="slide-menu-body">
              <div className="slide-menu-section">
                <Link to="/dashboard/accepted-jobs" className="slide-menu-item" onClick={navCollapse}>
                  My Diary
                </Link>
                <Link to="/dashboard" className="slide-menu-item" onClick={navCollapse}>
                  Available Jobs {availableJobsCount.inMyArea + availableJobsCount.outsideMyArea + availableJobsCount.nationalJobs > 0 ? `(${availableJobsCount.inMyArea}/${availableJobsCount.outsideMyArea}/${availableJobsCount.nationalJobs})` : ''}
                </Link>
                <Link to="/dashboard/referrals" className="slide-menu-item" onClick={navCollapse}>
                  Refer a Friend
                </Link>
              </div>
              
              <div className="slide-menu-section">
                <h6 
                  className={`slide-menu-section-title ${reportsExpanded ? 'expanded' : ''}`}
                  onClick={() => setReportsExpanded(!reportsExpanded)}
                >
                  <span>Reports</span>
                  <span className="collapse-icon">▶</span>
                </h6>
                <div className={`slide-menu-section-content ${reportsExpanded ? 'expanded' : ''}`}>
                  <Link to="/dashboard/completed-jobs" className="slide-menu-item" onClick={navCollapse}>
                    Completed Jobs
                  </Link>
                  <Link to="/dashboard/uncompleted-jobs" className="slide-menu-item" onClick={navCollapse}>
                    UnCompleted Jobs
                  </Link>
                  <Link to="/dashboard/reassigned-jobs" className="slide-menu-item" onClick={navCollapse}>
                    Assigned Jobs
                  </Link>
                  <Link to="/dashboard/driver-stats" className="slide-menu-item" onClick={navCollapse}>
                    Driver Dashboard
                  </Link>
                </div>
              </div>
              
              <div className="slide-menu-section">
                <h6 
                  className={`slide-menu-section-title ${adminExpanded ? 'expanded' : ''}`}
                  onClick={() => setAdminExpanded(!adminExpanded)}
                >
                  <span>Admin</span>
                  <span className="collapse-icon">▶</span>
                </h6>
                <div className={`slide-menu-section-content ${adminExpanded ? 'expanded' : ''}`}>
                  <Link to="/dashboard/update-account" className="slide-menu-item" onClick={navCollapse}>
                    Profile
                  </Link>
                  <Link to="/dashboard/job-monitor" className="slide-menu-item" onClick={navCollapse}>
                    Job Monitoring Settings
                  </Link>
                  <Link to="/dashboard/booking-links" className="slide-menu-item" onClick={navCollapse}>
                    Booking Links
                  </Link>
                  <Link to="/dashboard/merchandise" className="slide-menu-item" onClick={navCollapse}>
                    Order Merchandise
                  </Link>
                  <Link to="/dashboard/email-log" className="slide-menu-item" onClick={navCollapse}>
                    Email Log
                  </Link>
                  <Link to="/dashboard/customer-management" className="slide-menu-item" onClick={navCollapse}>
                    Customer Management
                  </Link>
                  <Link to="/dashboard/my-reviews" className="slide-menu-item" onClick={navCollapse}>
                    My Reviews
                  </Link>
                </div>
              </div>
              
              <div className="slide-menu-section">
                <h6 
                  className={`slide-menu-section-title ${supportExpanded ? 'expanded' : ''}`}
                  onClick={() => setSupportExpanded(!supportExpanded)}
                >
                  <span>Support</span>
                  <span className="collapse-icon">▶</span>
                </h6>
                <div className={`slide-menu-section-content ${supportExpanded ? 'expanded' : ''}`}>
                  <Link to="/dashboard/training" className="slide-menu-item" onClick={navCollapse}>
                    Training
                  </Link>
                  <Link to="/dashboard/troubleshooting" className="slide-menu-item" onClick={navCollapse}>
                    Troubleshooting
                  </Link>
                  <Link to="/dashboard/downloads" className="slide-menu-item" onClick={navCollapse}>
                    Downloads
                  </Link>
                </div>
              </div>
              
              <div className="slide-menu-footer">
                <button 
                  className="slide-menu-logout-btn"
                  onClick={() => {
                    closeSlideMenu();
                    logout();
                  }}
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </nav>
  );
};

export default Navigation;
